import React from 'react';
import { useTranslation } from 'react-i18next';
import { Quote, ChildQuote, QuoteJob, QuoteTask } from '../../types/quote';
import { Job, JobQuote } from '../../types/job';
import FormattedCurrency from '../common/FormattedCurrency';
import ProfileAvatar from '../common/ProfileAvatar';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { formatDateLocalized } from '../../utils/dateUtils';
import { computeQuoteTotalAmount, computeQuoteAmount } from '../../utils/jobUtils';
import { ReactComponent as PigeonsLogotype } from '../../assets/images/pigeons_logotype1.svg';
import ProfileBioText from '../common/ProfileBioText';

const TASK_TYPES_WITH_QUANTITY = ['hourly', 'unit'];

function taskLineTotal(task: QuoteTask): number {
  if (TASK_TYPES_WITH_QUANTITY.includes(task.pricing_type)) {
    return Number(task.quantity ?? 0) * Number(task.unit_price ?? 0);
  }
  return Number(task.unit_price ?? 0);
}

interface TaskTableProps {
  tasks: QuoteTask[];
  subTotal: number;
  subTotalLabel: string;
}

function TaskTable({ tasks, subTotal, subTotalLabel }: TaskTableProps) {
  const { t } = useTranslation(['quotes']);
  const headerClass =
    'text-[0.65rem] font-bold uppercase tracking-wide text-secondary border-b-2 border-[#0D2D39] pb-1';
  const cellClass = 'text-[0.8rem] text-secondary py-2 border-b border-[#E1E9F0] align-top';

  return (
    <div className="grid grid-cols-[3fr_1fr_0.6fr_1fr_1fr] gap-x-3 items-start">
      <span className={headerClass}>
        {t('createQuoteModal.taskDescriptionColumnHeader', {
          defaultValue: 'Description de la tâche',
        })}
      </span>
      <span className={headerClass}>
        {t('createQuoteModal.typeColumnHeader', { defaultValue: 'Type' })}
      </span>
      <span className={headerClass}>
        {t('createQuoteModal.quantityColumnHeader', { defaultValue: 'Qté' })}
      </span>
      <span className={headerClass}>
        {t('createQuoteModal.unitPriceColumnHeader', { defaultValue: 'Prix unitaire' })}
      </span>
      <span className={`${headerClass} text-right`}>
        {t('createQuoteModal.totalLabel', { defaultValue: 'Total' })}
      </span>

      {tasks.map((task, idx) => {
        const quantityVisible = TASK_TYPES_WITH_QUANTITY.includes(task.pricing_type);
        return (
          <React.Fragment key={task.id ?? idx}>
            <span className={`${cellClass} break-words`}>{task.name || '—'}</span>
            <span className={cellClass}>
              {t(`createQuoteModal.pricingType.${task.pricing_type}`)}
            </span>
            <span className={cellClass}>{quantityVisible ? task.quantity : '—'}</span>
            <span className={cellClass}>
              <FormattedCurrency value={task.unit_price} />
            </span>
            <span className={`${cellClass} text-right font-semibold text-primary`}>
              <FormattedCurrency value={task.total_amount ?? taskLineTotal(task)} />
            </span>
          </React.Fragment>
        );
      })}

      <span className="col-span-4 text-[0.8rem] font-bold text-secondary text-right pt-3">
        {subTotalLabel}
      </span>
      <span className="text-[0.9rem] font-bold text-primary text-right pt-3">
        <FormattedCurrency value={subTotal} />
      </span>
    </div>
  );
}

interface FreelancerBlockProps {
  owner: Quote['quote_owner'];
  submittedAt: string | null;
}

function FreelancerBlock({ owner, submittedAt }: FreelancerBlockProps) {
  const { t, i18n } = useTranslation(['quotes', 'settings', 'freelancers', 'common']);
  const submissionDate = submittedAt
    ? formatDateLocalized(submittedAt, i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
  const services = getFreelancerSubtitle(
    { service_category_slugs: owner?.services?.map(s => s.slug) },
    t
  );

  return (
    <div className="flex gap-4 items-start">
      <ProfileAvatar
        profile_id={owner.id}
        first_name={owner.first_name}
        avatar_url={owner.avatar_url}
        className="w-16 h-16 flex-shrink-0"
      />
      <div className="flex-1">
        <div className="text-base font-bold text-secondary leading-tight">
          {owner.first_name} {owner.last_name}
        </div>
        {services && <div className="text-[0.8rem] text-secondary mt-1">{services}</div>}
        {submissionDate && (
          <div className="text-[0.7rem] text-secondary/70 mt-1">
            {t('quoteRequestInfo.submittedDate', { ns: 'quotes' })}: {submissionDate}
          </div>
        )}
        {owner.bio && (
          <p className="text-[0.8rem] text-secondary mt-2 whitespace-pre-wrap">
            <ProfileBioText bio={owner.bio} profileId={owner.id} skipRedaction={true} />
          </p>
        )}
      </div>
    </div>
  );
}

interface PrintableBidViewProps {
  quote: Quote | ChildQuote | JobQuote;
  job: QuoteJob | Job;
}

export const PrintableBidView: React.FC<PrintableBidViewProps> = ({ quote, job }) => {
  const { t, i18n } = useTranslation(['quotes', 'jobs', 'common', 'freelancers', 'settings']);

  const teamMembers =
    'alliances' in quote ? quote.alliances.filter(a => a.child_quote).map(a => a.child_quote!) : [];
  const hasTeamMembers = teamMembers.length > 0;

  const totalAmount = computeQuoteTotalAmount(quote);
  const mainSubtotal = computeQuoteAmount(quote);

  const quoteShortId = String(quote.id).split('-')[0]?.toUpperCase() ?? quote.id;
  const generatedOn = formatDateLocalized(new Date(), i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const jobStartDate =
    'start_date' in job && job.start_date
      ? formatDateLocalized(job.start_date, i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;
  const jobEndDate =
    'end_date' in job && job.end_date
      ? formatDateLocalized(job.end_date, i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;

  const clientDisplay =
    job.client?.company_name?.trim() ||
    `${job.client?.first_name ?? ''} ${job.client?.last_name ?? ''}`.trim();

  const sectionTitle = 'text-[0.65rem] font-bold uppercase tracking-wider text-primary mb-2';
  const pageBreakInside = 'print:break-inside-avoid';

  return (
    <div className="bg-white text-secondary font-ambit max-w-[52rem] mx-auto p-8 md:p-12 print:p-0">
      {/* Letterhead */}
      <header className="flex items-start justify-between border-b-2 border-secondary pb-4 mb-6">
        <div className="flex items-center gap-3">
          <PigeonsLogotype className="h-10 w-auto" aria-label="Pigeons" />
          <div>
            <div className="text-lg font-bold text-secondary leading-none">Pigeons</div>
            <div className="text-[0.7rem] text-secondary/70 mt-1">nouspigeons.ca</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[0.7rem] uppercase tracking-wider text-secondary/70">
            {t('print.documentLabel', { ns: 'quotes', defaultValue: 'Soumission' })}
          </div>
          <div className="text-sm font-bold text-secondary mt-1">#{quoteShortId}</div>
          {quote.submitted_at && (
            <div className="text-[0.7rem] text-secondary/70 mt-1">
              {formatDateLocalized(quote.submitted_at, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          )}
        </div>
      </header>

      {/* Project */}
      <section className={`mb-6 ${pageBreakInside}`}>
        <h2 className={sectionTitle}>
          {t('quoteRequestInfo.projectInfo', { ns: 'quotes', defaultValue: 'Projet' })}
        </h2>
        <div className="text-lg font-bold text-secondary">{job.title}</div>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[0.8rem] text-secondary">
          {clientDisplay && (
            <div>
              <dt className="inline font-semibold">
                {t('quoteRequestInfo.companyName', { ns: 'quotes', defaultValue: 'Client' })}:{' '}
              </dt>
              <dd className="inline">{clientDisplay}</dd>
            </div>
          )}
          {job.city?.name && (
            <div>
              <dt className="inline font-semibold">
                {t('quoteRequestInfo.city', { ns: 'quotes', defaultValue: 'Lieu' })}:{' '}
              </dt>
              <dd className="inline">{job.city.name}</dd>
            </div>
          )}
          {jobStartDate && (
            <div>
              <dt className="inline font-semibold">
                {t('quoteRequestInfo.startDate', { ns: 'quotes', defaultValue: 'Début' })}:{' '}
              </dt>
              <dd className="inline">{jobStartDate}</dd>
            </div>
          )}
          {jobEndDate && (
            <div>
              <dt className="inline font-semibold">
                {t('quoteRequestInfo.dueDate', { ns: 'quotes', defaultValue: 'Échéance' })}:{' '}
              </dt>
              <dd className="inline">{jobEndDate}</dd>
            </div>
          )}
          {typeof job.on_site === 'boolean' && (
            <div>
              <dt className="inline font-semibold">
                {t('quoteRequestInfo.onsite', { ns: 'quotes', defaultValue: 'Sur place' })}:{' '}
              </dt>
              <dd className="inline">
                {job.on_site
                  ? t('form_fields.yes', { ns: 'common', defaultValue: 'Oui' })
                  : t('form_fields.no', { ns: 'common', defaultValue: 'Non' })}
              </dd>
            </div>
          )}
        </dl>
        {job.description && (
          <p className="text-[0.8rem] text-secondary mt-3 whitespace-pre-wrap">{job.description}</p>
        )}
      </section>

      {/* Primary freelancer */}
      <section className={`mb-6 ${pageBreakInside}`}>
        <h2 className={sectionTitle}>
          {hasTeamMembers
            ? t('print.principalLabel', { ns: 'quotes', defaultValue: 'Soumissionnaire principal' })
            : t('print.freelancerLabel', { ns: 'quotes', defaultValue: 'Soumissionnaire' })}
        </h2>
        <FreelancerBlock owner={quote.quote_owner} submittedAt={quote.submitted_at} />
      </section>

      {/* Primary tasks */}
      {quote.tasks && quote.tasks.length > 0 && (
        <section className="mb-6">
          <h2 className={sectionTitle}>
            {t('print.tasksLabel', { ns: 'quotes', defaultValue: 'Détail des tâches' })}
          </h2>
          <TaskTable
            tasks={quote.tasks}
            subTotal={mainSubtotal}
            subTotalLabel={
              hasTeamMembers
                ? t('sub_total', { ns: 'quotes' })
                : t('createQuoteModal.totalAmountSectionTitle', { ns: 'quotes' })
            }
          />
        </section>
      )}

      {/* Team members (alliances) */}
      {hasTeamMembers && (
        <section className="mb-6">
          <h2 className={sectionTitle}>{t('teamMembers', { ns: 'quotes' })}</h2>
          <div className="space-y-6">
            {teamMembers.map((child, idx) => {
              const childSubtotal = child.tasks.reduce((s, t) => s + t.total_amount, 0);
              return (
                <div key={child.id ?? idx} className={`${pageBreakInside} pt-2`}>
                  <FreelancerBlock owner={child.quote_owner} submittedAt={child.submitted_at} />
                  {child.tasks && child.tasks.length > 0 && (
                    <div className="mt-3">
                      <TaskTable
                        tasks={child.tasks}
                        subTotal={childSubtotal}
                        subTotalLabel={t('sub_total', { ns: 'quotes' })}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Change request */}
      {'change_request_message' in quote &&
        quote.change_request_message &&
        quote.change_request_message.trim() !== '' && (
          <section className={`mb-6 ${pageBreakInside}`}>
            <h2 className={sectionTitle}>
              {t('createQuoteModal.changeRequestLabel', { ns: 'quotes' })}
            </h2>
            <p className="text-[0.8rem] text-secondary whitespace-pre-wrap">
              {quote.change_request_message}
            </p>
          </section>
        )}

      {/* Additional notes */}
      {'message' in quote && quote.message && quote.message.trim() !== '' && (
        <section className={`mb-6 ${pageBreakInside}`}>
          <h2 className={sectionTitle}>
            {t('createQuoteModal.additionalNotesLabel', { ns: 'quotes' })}
          </h2>
          <p className="text-[0.8rem] text-secondary whitespace-pre-wrap">{quote.message}</p>
        </section>
      )}

      {/* Grand total */}
      {totalAmount > 0 && (
        <section
          className={`mt-8 pt-4 border-t-2 border-secondary flex items-center justify-end gap-6 ${pageBreakInside}`}
        >
          <span className="text-sm font-bold uppercase tracking-wider text-secondary">
            {t('createQuoteModal.totalAmountSectionTitle', { ns: 'quotes' })}
          </span>
          <span className="text-2xl font-bold text-primary">
            <FormattedCurrency value={totalAmount} />
          </span>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-10 pt-4 border-t border-[#E1E9F0] text-[0.65rem] text-secondary/60 text-center">
        {t('print.footer', {
          ns: 'quotes',
          defaultValue: 'Document généré le {{date}} via Pigeons.',
          date: generatedOn,
        })}
      </footer>
    </div>
  );
};

export default PrintableBidView;
